import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress
} from '@mui/material';
import { Schedule, Public, LocationOn } from '@mui/icons-material';
import timezoneAPI from '../../services/timezoneAPI';

interface TimezoneData {
  detected_timezone: string;
  available_timezones: Record<string, string>;
}

interface UserTimezone {
  user_timezone: string;
  timezone_source: string;
}

const TimezoneSettings: React.FC = () => {
  const [timezoneData, setTimezoneData] = useState<TimezoneData | null>(null);
  const [userTimezone, setUserTimezone] = useState<UserTimezone | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchTimezoneData();
  }, []);

  const fetchTimezoneData = async () => {
    try {
      setLoading(true);
      
      // Fetch available timezones and detection using proper API service
      const detectionData = await timezoneAPI.detectTimezone();
      setTimezoneData(detectionData);
      
      // Try to get current user timezone (may fail if not authenticated)
      try {
        const currentData = await timezoneAPI.getCurrentUserTimezone();
        setUserTimezone(currentData);
        setSelectedTimezone(currentData.user_timezone);
      } catch (error) {
        // If user not authenticated, use detected timezone
        console.warn('User not authenticated, using detected timezone');
        const defaultTimezone = detectionData.detected_timezone || 'Asia/Kolkata';
        setUserTimezone({
          user_timezone: defaultTimezone,
          timezone_source: 'detected'
        });
        setSelectedTimezone(defaultTimezone);
      }
    } catch (error) {
      console.error('Error fetching timezone data:', error);
      setMessage({ type: 'error', text: 'Failed to load timezone settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedTimezone) return;

    try {
      setSaving(true);
      setMessage(null);

      const result = await timezoneAPI.updateTimezonePreference(selectedTimezone);
      setMessage({ type: 'success', text: result.message });
      
      // Refresh user timezone data
      await fetchTimezoneData();
    } catch (error) {
      console.error('Error saving timezone:', error);
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to save timezone preference' 
      });
    } finally {
      setSaving(false);
    }
  };

  const getCurrentTime = (timezone: string) => {
    try {
      const now = new Date();
      return now.toLocaleString('en-US', {
        timeZone: timezone,
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
    } catch (error) {
      return 'Invalid timezone';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Public color="primary" />
          <Typography variant="h6">Timezone Settings</Typography>
        </Box>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        {/* Current Timezone Info */}
        {userTimezone && (
          <Box mb={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Current Timezone
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <LocationOn fontSize="small" color="action" />
              <Typography variant="body1">
                {timezoneData?.available_timezones[userTimezone.user_timezone] || userTimezone.user_timezone}
              </Typography>
              <Chip
                size="small"
                label={userTimezone.timezone_source === 'user_preference' ? 'User Set' : 'Auto-Detected'}
                color={userTimezone.timezone_source === 'user_preference' ? 'primary' : 'default'}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Schedule fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {getCurrentTime(userTimezone.user_timezone)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Detected Timezone */}
        {timezoneData && (
          <Box mb={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Auto-Detected Timezone
            </Typography>
            <Typography variant="body2">
              {timezoneData.available_timezones[timezoneData.detected_timezone] || timezoneData.detected_timezone}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getCurrentTime(timezoneData.detected_timezone)}
            </Typography>
          </Box>
        )}

        {/* Timezone Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Timezone</InputLabel>
          <Select
            value={selectedTimezone}
            onChange={(e) => setSelectedTimezone(e.target.value)}
            label="Select Timezone"
          >
            {timezoneData?.available_timezones && Object.entries(timezoneData.available_timezones).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                <Box>
                  <Typography variant="body2">{value}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getCurrentTime(key)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Preview Selected Timezone */}
        {selectedTimezone && selectedTimezone !== userTimezone?.user_timezone && (
          <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="subtitle2" gutterBottom>
              Preview: {timezoneData?.available_timezones[selectedTimezone]}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Current time: {getCurrentTime(selectedTimezone)}
            </Typography>
          </Box>
        )}

        {/* Save Button */}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !selectedTimezone || selectedTimezone === userTimezone?.user_timezone}
          startIcon={saving ? <CircularProgress size={20} /> : <Public />}
        >
          {saving ? 'Saving...' : 'Save Timezone Preference'}
        </Button>

        {/* Info */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Your timezone preference affects how scheduled posts and analytics are displayed. 
          All times will be shown in your selected timezone.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default TimezoneSettings; 